/**
 * @file     dealFinderFlow.ts
 * @layer    src > core > config > dealFlows
 * @purpose  Defines the step sequences and analytics event names for the standard deal finder flow, the buyer-specific flow, and the pre-filled buyer flow.
 * @depends  @/core/types (DealFlowConfig)
 *
 * ──────────────────────────────────────────────────────────────────────────
 *  CHECKLIST: Adding or removing a deal-finder step
 * ──────────────────────────────────────────────────────────────────────────
 *  The deal-finder flow has a wide blast radius. A new step is not just a
 *  config tweak — it threads through ~15 layers. If you skip any of these,
 *  the failure mode is silent (TypeScript narrows the missing literal away,
 *  Postgres rejects inserts at runtime, analytics drops events on the floor).
 *  Walk this list top to bottom.
 *
 *  1. src/core/types/config.ts
 *     Add the step name to the `DealFlowStepName` union.
 *
 *  2. src/core/types/lead.ts
 *     If the step captures a new answer, add the corresponding enum
 *     (e.g. `DealFinderAmenity`) and add an optional field on
 *     `DealFinderQualification`.
 *
 *  3. src/features/deal-finder/types.ts
 *     Add the same field to `LeadFlowState` and extend the import block.
 *
 *  4. src/features/deal-finder/LeadFlowContext.tsx
 *     - new setter on `LeadFlowContextValue`
 *     - the setter implementation in `contextValue` (calls `updateState`)
 *     - `updateState` patch object (the `hasOwn(...)` shape map)
 *     - `qualification` builder (decide if the field is required or optional)
 *     - `fallbackInitialState` + `resolvedInitialState` (initialOverride seed)
 *     - `countMeaningfulFields` (resume-banner gate)
 *
 *  5. src/features/deal-finder/stepRegistry.tsx
 *     - import the new step component
 *     - add the setter to `DealFlowStepRenderContext`
 *     - destructure it in `renderDealFlowStep`
 *     - add the `case '<stepName>':` render branch
 *
 *  6. src/features/deal-finder/LeadFlowEngine.tsx
 *     - destructure the setter from `useLeadFlow()`
 *     - pass it into the `renderDealFlowStep` call site
 *
 *  7. src/features/deal-finder/steps/<NewStep>.tsx
 *     New step component. Re-export from steps/index.ts.
 *
 *  8. src/features/deal-finder/funnelTracker.ts
 *     - add the snake_case key to the `FunnelStepKey` union
 *     - map the camelCase step name in `dealFlowStepNameToFunnelKey`
 *
 *  9. src/app/api/deal-finder/track/route.ts
 *     Add the snake_case key to the `VALID_STEP_KEYS` Set, otherwise every
 *     funnel event for this step is silently rejected.
 *
 * 10. supabase/migrations/<timestamp>_<name>.sql
 *     Extend the `deal_finder_funnel_events.step_key` CHECK constraint to
 *     include the new key. Apply with `npx supabase db push`. Without this,
 *     inserts fail at the DB layer, not in the route — analytics shows zero
 *     events for the step and you won't notice for days.
 *
 * 11. src/features/deal-finder/leadTagging.ts
 *     If the answer should produce CRM tags, add a tag map and emit them in
 *     `buildLeadTags`.
 *
 * 12. src/features/deal-finder/steps/LeadCaptureStep.tsx
 *     Spread the new state field into BOTH submit payloads (the main
 *     `submitLead` call AND the partial-recovery call near the top).
 *
 * 13. src/app/api/leads/route.ts
 *     - add the field to the `LeadPayload` interface
 *     - forward it from `body.<field>` into the FUB function call (full + partial)
 *     - persist it on the Supabase `leads.form_data` row
 *
 * 14. src/services/crm/fubIntegration.ts
 *     Add a tag and a note line in both `submitDealFinderLeadToFub` and the
 *     partial variant. The note builder is what Ray actually reads in FUB —
 *     don't skip the human-readable line.
 *
 * 15. src/app/api/admin/funnel/route.ts
 *     If the step changes the canonical buyer-flow order, update
 *     `STEP_ORDER` in the route so the admin chart sorts steps correctly.
 *
 *  Removing a step is the same list in reverse. Do NOT delete enum values
 *  from `DealFinderIntent` / `DealFlowStepName` if there are persisted
 *  drafts, historical FUB tags, or deep-links that rely on them — prefer
 *  hiding from the UI (see IntentStep.tsx for the pattern: trim the visible
 *  options list, keep the type union intact).
 *
 *  When in doubt: grep for the existing step you're cloning. Every one of
 *  the 15 layers above will hit.
 * ──────────────────────────────────────────────────────────────────────────
 */
import type { DealFlowConfig, DealFlowStepName, StepConfig } from '@/core/types';
import { featureFlags } from '@/core/config/featureFlags';

/**
 * PR 3: when `featureFlags.criteriaReviewStep` is on, insert a 'criteriaReview'
 * pre-form preview step immediately before leadCapture in each flow. Gated by
 * flag so we can ship the migration + code path, then flip on after smoke test.
 * When off, the criteria_review step is simply absent from the step list and
 * existing in-flight sessions continue unchanged.
 */
function withCriteriaReview(
  steps: Array<StepConfig<DealFlowStepName>>,
): Array<StepConfig<DealFlowStepName>> {
  if (!featureFlags.criteriaReviewStep) return steps;
  const out: Array<StepConfig<DealFlowStepName>> = [];
  for (const step of steps) {
    const name = typeof step === 'string' ? step : step.name;
    if (name === 'leadCapture') {
      out.push('criteriaReview');
    }
    out.push(step);
  }
  return out;
}

/**
 * V2 (dealFinderStepRefinementsV2): when on, splits the legacy
 * 'sizeBedsBaths' step into 'bedsBaths' + 'squareFootage', and the legacy
 * 'location' step into 'locationParent' + 'locationSubZones'. When off, the
 * step list is returned unchanged (legacy behaviour).
 */
function applyV2StepRefinements(
  steps: Array<StepConfig<DealFlowStepName>>,
): Array<StepConfig<DealFlowStepName>> {
  if (!featureFlags.dealFinderStepRefinementsV2) return steps;
  const out: Array<StepConfig<DealFlowStepName>> = [];
  for (const step of steps) {
    const name = typeof step === 'string' ? step : step.name;
    if (name === 'sizeBedsBaths') {
      out.push('bedsBaths');
      out.push('squareFootage');
      continue;
    }
    if (name === 'location') {
      out.push({ name: 'locationParent', multiSelect: true });
      out.push({ name: 'locationSubZones', multiSelect: true });
      out.push({ name: 'locationNeighbourhoods', multiSelect: true });
      continue;
    }
    out.push(step);
  }
  return out;
}

export const dealFinderFlow: DealFlowConfig = {
  id: 'dealFinder',
  steps: applyV2StepRefinements(withCriteriaReview(['intent', 'timeline', { name: 'propertyType', multiSelect: true }, 'budget', { name: 'location', multiSelect: true }, 'leadCapture', 'confirmation'])),
  analytics: {
    start: 'lead_flow_started',
    stepCompleted: 'step_completed',
    resultsViewed: 'deal_results_viewed',
  }
};

export const buyerDealFlow: DealFlowConfig = {
  id: 'dealFinderBuyer',
  // Notes:
  // - 'timeline' was removed from the buyer flow because it caused mid-funnel drop-off —
  //   buyers without a fixed timeline interpreted the question as a commitment and bailed.
  //   Sellers still answer it (sellerFlow.ts) where timing genuinely shapes pricing/prep advice.
  // - 'amenities' (multi-select) sits right after 'propertyType' because it builds momentum:
  //   the user has just told us what they want; amenities feels like another tap, not a question.
  // - 'cadence' captures the user's preferred shortlist update rhythm immediately before
  //   contact capture so the last screen before "give me your details" feels like *you*
  //   picking the relationship, not us qualifying you.
  // PR 1 buyer-flow update (2026-05-03):
  // - Insert 'sizeBedsBaths' after propertyType (required composite)
  // - Insert 'layout' before amenities (optional, multi-select with Skip)
  // - 'buildingConcerns' after location (optional, multi-select with Skip)
  // - 'viewingStyle' before lead capture (optional, single-select with Skip)
  // - Drop 'cadence' from buyer flow (replaced by simpler trust line on lead capture)
  // PR 3 wraps the steps with withCriteriaReview() to insert 'criteriaReview'
  // immediately before 'leadCapture' when the flag is on.
  steps: applyV2StepRefinements(withCriteriaReview([
    'intent',
    'presaleUse',
    'depositReadiness',
    { name: 'propertyType', multiSelect: true },
    'sizeBedsBaths',
    { name: 'layout', multiSelect: true },
    { name: 'amenities', multiSelect: true },
    'budget',
    { name: 'location', multiSelect: true },
    { name: 'buildingConcerns', multiSelect: true },
    'viewingStyle',
    'leadCapture',
    'confirmation'
  ])),
  analytics: {
    start: 'lead_flow_started',
    stepCompleted: 'step_completed',
    resultsViewed: 'deal_results_viewed',
  }
};

export const prefilledBuyerDealFlow: DealFlowConfig = {
  id: 'dealFinderBuyerPrefilled',
  steps: applyV2StepRefinements(withCriteriaReview([
    'presaleUse',
    'depositReadiness',
    { name: 'propertyType', multiSelect: true },
    'sizeBedsBaths',
    { name: 'layout', multiSelect: true },
    { name: 'amenities', multiSelect: true },
    'budget',
    { name: 'location', multiSelect: true },
    { name: 'buildingConcerns', multiSelect: true },
    'viewingStyle',
    'leadCapture',
    'confirmation'
  ])),
  analytics: {
    start: 'lead_flow_started',
    stepCompleted: 'step_completed',
    resultsViewed: 'deal_results_viewed',
  }
};

/**
 * Default flow on /deal-finder/buyer (Ray, 2026-10-29: the lite flow was too
 * short to trust). Keeps the questions that change which homes match and
 * leaves the rest (size, layout, building concerns, viewing style) to
 * follow-up. Location is two levels, region then areas; neighbourhoods are
 * left out. Presale steps only render on the presale journey. No
 * criteriaReview: its preview reads the legacy `properties` table, which has
 * no active rows.
 */
export const buyerGuidedFlow: DealFlowConfig = {
  id: 'dealFinderBuyerGuided',
  steps: [
    'presaleUse',
    'depositReadiness',
    { name: 'propertyType', multiSelect: true },
    'bedsBaths',
    'budget',
    { name: 'locationParent', multiSelect: true },
    { name: 'locationSubZones', multiSelect: true },
    { name: 'amenities', multiSelect: true },
    'leadCapture',
    'confirmation',
  ],
  analytics: {
    start: 'lead_flow_started',
    stepCompleted: 'step_completed',
    resultsViewed: 'deal_results_viewed',
  },
};

/**
 * Lite flow embedded inline under landing-page heroes. Three input steps:
 * location, price, lead capture. Intent is implicit on /l/* (the user clicked
 * an ad for a specific region = buy-home) and pre-filled via initialState on
 * LandingPageView, so asking it again is dead-weight friction. PropertyType
 * is also pre-filled to 'not-sure-yet'. Heavy qualification is deferred to
 * FUB follow-up enrichment. Does NOT apply V2 refinements so location stays
 * a single step.
 */
export const dealFinderLiteFlow: DealFlowConfig = {
  id: 'dealFinderLite',
  steps: [
    { name: 'location', multiSelect: true },
    'budget',
    'leadCapture',
    'confirmation',
  ],
  analytics: {
    start: 'lead_flow_started',
    stepCompleted: 'step_completed',
    resultsViewed: 'deal_results_viewed',
  },
};
